/**
 * Classifies frontend errors that ApiController.logFrontendError does not send
 * to the backend because there is nothing to act on (VIP-12640).
 */

import { getErrorMessage } from '@/services/helpers/ChunkLoadRecovery.js'

// Stricter than ChunkLoadRecovery's NETWORK_MESSAGE_REGEX: applies to any URL,
// not just assets, so a bare "NetworkError" (also a DOMException name) is not enough
const FETCH_ABORT_MESSAGE_REGEX = new RegExp(
  [
    'Failed to fetch', // Chrome
    'Load failed', // Safari
    'NetworkError when attempting to fetch resource', // Firefox
    // service worker had neither a network response nor a cached one
    String.raw`FetchEvent\.respondWith received an error`
  ].join('|')
)
// `"status":404` from RequestHelper's wrapper, `HTTP-404` from the inner error
const HTTP_STATUS_REGEX = /"status":\s*\d|HTTP-\d{3}/
const EMPTY_MESSAGES = new Set(['', 'undefined', 'null', '{}'])
// RequestHelper's wrapper around a message without status: `{"description":"…"}`
const WRAPPED_MESSAGE_START = '{"description":"'
const WRAPPED_MESSAGE_END = '"}'

/**
 * Client-side network aborts (ad blockers, offline, aborted navigations):
 * the request never got a response, so the backend has nothing to act on.
 * Matches anywhere in the message because RequestHelper wraps it, e.g.
 * `Error at cart [sendCouponCode]  {"description":"[https://…/coupon] Failed to fetch"}`.
 * A real HTTP error carries a status and is never treated as an abort (VIP-12640).
 * @param {unknown} reason - Rejection reason or error object
 * @returns {boolean}
 */
export function isNetworkFetchFailure(reason) {
  if (reason?.status != null) return false
  const msg = getErrorMessage(reason)
  return FETCH_ABORT_MESSAGE_REGEX.test(msg) && !HTTP_STATUS_REGEX.test(msg)
}

/**
 * True for reports without any content, e.g. `String(undefined)`; `{}` and
 * `{"description":""}` are what RequestHelper's wrapper yields for an error
 * without a message and status (VIP-12640).
 * @param {string} message - Message as it would be reported
 * @returns {boolean}
 */
export function isEmptyErrorMessage(message) {
  const trimmed = String(message ?? '').trim()
  if (EMPTY_MESSAGES.has(trimmed)) return true
  if (
    !trimmed.startsWith(WRAPPED_MESSAGE_START) ||
    !trimmed.endsWith(WRAPPED_MESSAGE_END)
  ) {
    return false
  }
  const inner = trimmed.slice(
    WRAPPED_MESSAGE_START.length,
    -WRAPPED_MESSAGE_END.length
  )
  return EMPTY_MESSAGES.has(inner.trim())
}
